import React, { useEffect } from "react";
import { useErrorModalStore } from "./errorModalStore";

type Props = {
  /** Esc로 닫기 허용 */
  closeOnEsc?: boolean;
  /** 배경 클릭으로 닫기 허용 */
  closeOnBackdrop?: boolean;
};

export default function ErrorModal({
  closeOnEsc = true,
  closeOnBackdrop = true,
}: Props) {
  const { isOpen, payload, close, clear } = useErrorModalStore();

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeOnEsc, close]);

  if (!isOpen || !payload) return null;

  const title = payload.title ?? "Error";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={styles.backdrop}
      onMouseDown={(e) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) close();
      }}
    >
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.title}>{title}</div>
          <button
            type="button"
            onClick={close}
            style={styles.iconButton}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={styles.body}>
          {payload.code != null && (
            <div style={styles.meta}>
              <span style={styles.badge}>CODE</span>
              <span style={styles.metaText}>{String(payload.code)}</span>
            </div>
          )}

          <div style={styles.message}>{payload.message}</div>

          {payload.detail && <pre style={styles.detail}>{payload.detail}</pre>}
        </div>

        <div style={styles.footer}>
          <button type="button" onClick={clear} style={styles.primaryButton}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 10010,
  },
  modal: {
    width: "min(520px, 100%)",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
  },
  iconButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontSize: 20,
    lineHeight: "30px",
  },
  body: {
    padding: 16,
    color: "#0f172a",
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 8px",
    borderRadius: 999,
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
  },
  metaText: {
    fontSize: 12,
    color: "#334155",
  },
  message: {
    fontSize: 14,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  detail: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: "#0b1220",
    color: "#e2e8f0",
    overflow: "auto",
    maxHeight: 220,
    fontSize: 12,
    lineHeight: 1.5,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: 16,
    borderTop: "1px solid #e5e7eb",
    background: "#fff",
  },
  primaryButton: {
    border: "1px solid #0f172a",
    background: "#0f172a",
    color: "#fff",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
};
